import Pagination from '../../components/Common/Pagination';
import React, { useState, useMemo } from 'react';
import { ArrowLeft } from 'lucide-react';
import CSVButton from '../../components/Common/CSVButton';
import { exportToCSV } from '../../utils/csvExport';

interface Collateral {
  id: number;
  name: string;
  url: string;
  skuUnit: string;
  assets: string;
  opportunity: string;
  collateralType: 'SKU' | 'Assets' | 'Opportunities';
  sequence: string;
  status: 'Active' | 'Inactive';
}

export const CollateralsList: React.FC = () => {
  const [collaterals, setCollaterals] = useState<Collateral[]>([]);
  const [view, setView] = useState<'list' | 'add' | 'edit'>('list');
  const [_editingCollateral, setEditingCollateral] = useState<Collateral | null>(null);

  const [activeTab, setActiveTab] = useState<'Active' | 'Inactive' | 'All'>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [entriesLimit, setEntriesLimit] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    collateralType: 'SKU' as 'SKU' | 'Assets' | 'Opportunities',
    skuUnit: '',
    mediaType: '',
    category: '',
    filterType: '',
  });

  const activeCounts = 445; // Fixed mock count as shown in figma
  const inactiveCounts = 445; // Fixed mock count as shown in figma

  // Filtering Logic
  const filteredCollaterals = useMemo(() => {
    return collaterals.filter((item) => {
      if (activeTab === 'Active' && item.status !== 'Active') return false;
      if (activeTab === 'Inactive' && item.status !== 'Inactive') return false;

      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          item.name.toLowerCase().includes(query) ||
          item.skuUnit.toLowerCase().includes(query)
        );
      }
      return true;
    });
  }, [collaterals, activeTab, searchQuery]);

  // Paginated Collaterals
  const paginatedCollaterals = useMemo(() => {
    const startIndex = (currentPage - 1) * entriesLimit;
    return filteredCollaterals.slice(startIndex, startIndex + entriesLimit);
  }, [filteredCollaterals, currentPage, entriesLimit]);

  const handleOpenAdd = () => {
    setEditingCollateral(null);
    setFormData({
      name: '',
      collateralType: 'SKU',
      skuUnit: '',
      mediaType: '',
      category: '',
      filterType: '',
    });
    setView('add');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) {
      alert('Name is required.');
      return;
    }

    const newItem: Collateral = {
      id: collaterals.length > 0 ? Math.max(...collaterals.map(c => c.id)) + 1 : 1,
      name: formData.name,
      url: '-',
      skuUnit: formData.skuUnit || '-',
      assets: formData.collateralType === 'Assets' ? 'Yes' : '-',
      opportunity: formData.collateralType === 'Opportunities' ? 'Yes' : '-',
      collateralType: formData.collateralType,
      sequence: String(collaterals.length),
      status: 'Active',
    };

    setCollaterals([...collaterals, newItem]);
    setView('list');
  };

  if (view === 'add' || view === 'edit') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <button 
            type="button"
            onClick={() => setView('list')}
            className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm" title="Go Back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight">
            Add Collaterals
          </h1>
        </div>
        </div>

        {/* Form Container */}
        <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 rounded-xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
            {/* Name */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">Name</label>
              <input 
                type="text" 
                required
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full border border-[#828282] rounded-2xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-800" 
              />
            </div>

            {/* Collateral Type */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">Collateral Type</label>
              <div className="flex gap-3">
                {(['SKU', 'Assets', 'Opportunities'] as const).map((type) => {
                  const isSelected = formData.collateralType === type;
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setFormData({...formData, collateralType: type})}
                      className={`flex-1 py-2.5 rounded-xl text-xs font-bold transition-all border border-2xl text-center ${
                        isSelected 
                          ? 'bg-[#DCA10D] text-white border-[#DCA10D] '
                          : 'bg-white text-slate-650 border-[#828282] hover:bg-slate-50'
                      }`}
                    >
                      {type}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Search SKU Unit */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">Search SKU Unit</label>
              <div className="relative">
                <select
                  value={formData.skuUnit}
                  onChange={(e) => setFormData({...formData, skuUnit: e.target.value})}
                  className="w-full border border-[#828282] rounded-2xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-700 appearance-none bg-white"
                >
                  <option value="">Please Select</option>
                  <option value="SKU 1">SKU 1</option>
                  <option value="SKU 2">SKU 2</option>
                </select>
                <span className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </div>
            </div>

            {/* Collateral Media Type */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-0.5">Collateral Media Type</label>
              <span className="block text-[10px] text-slate-500 mb-1">Video, Image, Hyperlink, Pdf, Thumbnail</span>
              <div className="relative">
                <select
                  value={formData.mediaType}
                  onChange={(e) => setFormData({...formData, mediaType: e.target.value})}
                  className="w-full border border-[#828282] rounded-2xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-700 appearance-none bg-white"
                >
                  <option value="">Please Select</option>
                  <option value="Video">Video</option>
                  <option value="Image">Image</option>
                  <option value="Pdf">Pdf</option>
                  <option value="Hyperlink">Hyperlink</option>
                </select>
                <span className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </div>
            </div>

            {/* Categories */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">Categories</label>
              <div className="relative">
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="w-full border border-[#828282] rounded-2xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-700 appearance-none bg-white"
                >
                  <option value="">Please Select</option>
                  <option value="Category 1">Category 1</option>
                  <option value="Category 2">Category 2</option>
                </select>
                <span className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </div>
            </div>

            {/* Filter Type */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">Filter Type</label>
              <div className="relative">
                <select
                  value={formData.filterType}
                  onChange={(e) => setFormData({...formData, filterType: e.target.value})}
                  className="w-full border border-[#828282] rounded-2xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-700 appearance-none bg-white"
                >
                  <option value="">Please Select</option>
                  <option value="Filter 1">Filter 1</option>
                  <option value="Filter 2">Filter 2</option>
                </select>
                <span className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </div>
            </div>

            {/* Upload Collateral */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">Upload Collateral</label>
              <div className="flex items-center border border-[#828282] rounded-2xl overflow-hidden bg-white">
                <button 
                  type="button" 
                  className="go_erp-bg text-white text-xs font-bold py-2.5 px-4 transition-colors"
                >
                  Upload
                </button>
                <input 
                  type="text" 
                  readOnly
                  placeholder="Choose file" 
                  className="flex-1 bg-transparent px-3 text-xs text-slate-500 outline-none cursor-default font-semibold"
                />
                <button 
                  type="button" 
                  className="bg-[#FFF8E1] text-slate-700 hover:bg-[#FFF3CD] text-xs font-bold py-2.5 px-4 transition-colors"
                >
                  Browse
                </button>
              </div>
            </div>

            {/* Thumbnail for Collateral */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">Thumbnail for Collateral</label>
              <div className="flex items-center border border-[#828282] rounded-2xl overflow-hidden bg-white">
                <button 
                  type="button" 
                  className="go_erp-bg text-white text-xs font-bold py-2.5 px-4 transition-colors"
                >
                  Upload
                </button>
                <input 
                  type="text" 
                  readOnly
                  placeholder="Choose file" 
                  className="flex-1 bg-transparent px-3 text-xs text-slate-500 outline-none cursor-default font-semibold"
                />
                <button 
                  type="button" 
                  className="bg-[#FFF8E1] text-slate-700 hover:bg-[#FFF3CD] text-xs font-bold py-2.5 px-4 transition-colors"
                >
                  Browse
                </button>
              </div>
            </div>

          </div>

        </form>
          {/* Submit */}
          <div className="flex justify-center pt-4">
            <button 
              type="submit" 
              className="bg-[#DCA10D] hover:bg-[#c28c00] text-white font-bold py-3 px-16 rounded-xl text-sm transition-all cursor-pointer shadow-md hover:shadow-[#DCA10D]/20"
            >
              Submit
            </button>
          </div>
      </div>
    );
  }

  const handleExportCSV = () => {
    const headers = ['ID', 'Name', 'SKU/Unit', 'Assets', 'Opportunity', 'Type', 'Sequence', 'Status'];
    const rows = filteredCollaterals.map((c) => [
      c.id,
      c.name,
      c.skuUnit,
      c.assets,
      c.opportunity,
      c.collateralType,
      c.sequence,
      c.status,
    ]);
    exportToCSV(`collaterals_${activeTab.toLowerCase()}`, headers, rows);
  };

  return (
    <div className="space-y-6">
      {/* Control Row */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        {/* Title and Back */}
        <div className="flex items-center gap-3">
          <button 
            onClick={() => window.history.back()}
            className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm" title="Go Back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight">
            Collaterals
          </h1>
        </div>

        {/* Status Tabs */}
        <div className="flex bg-white p-1 rounded-full border border-slate-200 w-fit">
          {(['All', 'Active', 'Inactive'] as const).map((tab) => {
            const isActive = activeTab === tab;
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-1.5 rounded-full text-xs font-bold transition-all duration-150 cursor-pointer ${
                  isActive
                    ? 'go_erp-bg text-white'
                    : 'text-slate-655 hover:text-slate-900'
                }`}
              >
                {tab}
              </button>
            );
          })}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2.5">
          <button className="bg-[#5D4037] hover:bg-[#4E342E] text-white text-sm px-4 py-2.5 rounded-xl cursor-pointer transition-colors shadow-sm">
            Deactivate
          </button>
          <button className="bg-[#F9C327] hover:bg-[#E5B21E] text-white text-sm px-4 py-2.5 rounded-xl cursor-pointer transition-colors shadow-sm">
            Activate
          </button>
          <button
            onClick={handleOpenAdd}
            className="go_erp-bg text-white text-sm px-4 py-2.5 rounded-xl flex items-center gap-2 cursor-pointer transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Add Collateral
          </button>
          <CSVButton onClick={handleExportCSV} />
        </div>
      </div>

      {/* Counts */}
      <div className="flex items-center justify-end gap-4 text-xs font-bold text-slate-800">
        <span>Active Counts: <span className="font-extrabold">{activeCounts}</span></span>
        <span>Inactive Counts: <span className="font-extrabold">{inactiveCounts}</span></span>
      </div>

      {/* Table Main Card */}
      <div className="bg-white rounded-3xl overflow-hidden">
        
        {/* Table Toolbar */}
        <div className="px-6 py-4  flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-slate-50/50">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-500">Show</span>
            <select
              value={entriesLimit}
              onChange={(e) => setEntriesLimit(Number(e.target.value))}
              className="bg-white border border-slate-200 text-xs font-bold text-slate-700 rounded px-2 py-1 outline-none focus:border-slate-400"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span className="text-xs font-semibold text-slate-500">entries</span>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-white border border-slate-200 text-xs rounded-full py-1.5 pl-8 pr-3 outline-none focus:border-slate-400 w-44 transition-all"
              />
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </span>
            </div>
          </div>
        </div>

        {/* Table Wrapper */}
        <div className="overflow-x-auto select-none">
          <table className="w-full text-left border-collapse min-w-[1000px]">
            <thead>
              <tr className="bg-[#FFF8E1]">
                <th className="px-4 py-4 w-10 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  <input type="checkbox" className="rounded go_erp-text focus:ring-[#C62828]" />
                </th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">ID</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">Name</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">URL</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">SKU Unit</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">Assets</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">Opportunity</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">Collateral Type</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">Sequence</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">Status</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f] text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#ECECF2]">
              {paginatedCollaterals.length > 0 ? (
                paginatedCollaterals.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-4">
                      <input type="checkbox" className="rounded go_erp-text focus:ring-[#C62828]" />
                    </td>
                    <td className="px-4 py-4 text-xs text-[#1f1f1f]">{item.id}</td>
                    <td className="px-4 py-4 text-xs text-[#1f1f1f]">{item.name}</td>
                    <td className="px-4 py-4 text-xs text-[#1f1f1f]">{item.url}</td>
                    <td className="px-4 py-4 text-xs text-[#1f1f1f]">{item.skuUnit}</td>
                    <td className="px-4 py-4 text-xs text-[#1f1f1f]">{item.assets}</td>
                    <td className="px-4 py-4 text-xs text-[#1f1f1f]">{item.opportunity}</td>
                    <td className="px-4 py-4 text-xs text-[#1f1f1f]">{item.collateralType}</td>
                    <td className="px-4 py-4 text-xs text-[#1f1f1f]">{item.sequence}</td>
                    <td className="px-4 py-4 text-xs text-[#1f1f1f]">{item.status}</td>
                    <td className="px-4 py-4 text-xs text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button className="p-1.5 text-slate-500 hover:text-[#B71C1C] transition-colors cursor-pointer rounded-lg" title="View">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                          </button>
                          <button className="p-1.5 text-slate-500 hover:text-blue-600 transition-colors cursor-pointer rounded-lg" title="Edit">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                          </button>
                        </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={11} className="px-6 py-14 text-center text-xs font-semibold text-slate-400">
                    No Data Available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <Pagination
          currentPage={currentPage}
          totalEntries={filteredCollaterals.length}
          entriesLimit={entriesLimit}
          onPageChange={setCurrentPage}
        />

      </div>
    </div>
  );
};
export default CollateralsList;
